import React, { useState, useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import './ApiTestPage.css';

const ApiTestPage = () => {
  const { getTokenInfo, userProfile } = useContext(AuthContext);
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [requestData, setRequestData] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customParams, setCustomParams] = useState({});

  const endpoints = {
    // Authentication
    'GET /login': {
      method: 'GET',
      url: '/login',
      description: 'Start Azure AD OAuth flow',
      requiresAuth: false,
      params: {}
    },
    'GET /callback': {
      method: 'GET',
      url: '/callback',
      description: 'OAuth callback handler',
      requiresAuth: false,
      params: {}
    },
    'GET /api/me': {
      method: 'GET',
      url: '/api/me',
      description: 'Get current user info',
      requiresAuth: true,
      params: {}
    },
    'GET /api/profile': {
      method: 'GET',
      url: '/api/profile',
      description: 'Get user profile from JWT token',
      requiresAuth: true,
      params: {}
    },
    'GET /api/validate-token': {
      method: 'GET',
      url: '/api/validate-token',
      description: 'Validate JWT token (also supports POST)',
      requiresAuth: false,
      params: {}
    },
    'POST /api/validate-token': {
      method: 'POST',
      url: '/api/validate-token',
      description: 'Validate JWT token (also supports GET)',
      requiresAuth: false,
      params: {}
    },
    'POST /api/refresh-token': {
      method: 'POST',
      url: '/api/refresh-token',
      description: 'Refresh JWT token with current database roles',
      requiresAuth: true,
      params: {}
    },
    'POST /api/logout': {
      method: 'POST',
      url: '/api/logout',
      description: 'API logout endpoint (also supports GET)',
      requiresAuth: false,
      params: {}
    },
    'GET /api/logout': {
      method: 'GET',
      url: '/api/logout',
      description: 'API logout endpoint (also supports POST)',
      requiresAuth: false,
      params: {}
    },

    // Company Management
    'GET /api/companies': {
      method: 'GET',
      url: '/api/companies',
      description: 'List companies (ADMIN only)',
      requiresAuth: true,
      params: {}
    },
    'GET /api/companies/<id>': {
      method: 'GET',
      url: '/api/companies/{id}',
      description: 'Get company by ID (ADMIN only)',
      requiresAuth: true,
      params: { id: '1' }
    },

    // Game Management
    'GET /api/games': {
      method: 'GET',
      url: '/api/games',
      description: 'List games',
      requiresAuth: true,
      params: {}
    },
    'GET /api/company-games': {
      method: 'GET',
      url: '/api/company-games',
      description: 'List company games',
      requiresAuth: true,
      params: {}
    },

    // Submissions
    'GET /api/submissions/my': {
      method: 'GET',
      url: '/api/submissions/my',
      description: 'Get my submissions (optional: companyGameId query parameter)',
      requiresAuth: true,
      params: { companyGameId: '1' }
    },

    // Reporting
    'GET /api/reports/users/<user_id>': {
      method: 'GET',
      url: '/api/reports/users/{user_id}',
      description: 'Get user report card',
      requiresAuth: true,
      params: { user_id: '123' }
    },
    'GET /api/reports/today': {
      method: 'GET',
      url: '/api/reports/today',
      description: 'Get today\'s roster (ADMIN/EDITOR)',
      requiresAuth: true,
      params: { company_game_id: '1' }
    },

    // Legacy API
    'GET /api/config': {
      method: 'GET',
      url: '/api/config',
      description: 'Get calendar metadata',
      requiresAuth: false,
      params: {}
    },
    'GET /api/current-date': {
      method: 'GET',
      url: '/api/current-date',
      description: 'Get current date from server',
      requiresAuth: false,
      params: {}
    },
    'GET /api/days/<day_number>/content': {
      method: 'GET',
      url: '/api/days/{day_number}/content',
      description: 'Get day content (legacy) (returns: content, contentType, day, title, contentFlags)',
      requiresAuth: false,
      params: { day_number: '1' }
    },
    'POST /api/days/<day_number>/validate': {
      method: 'POST',
      url: '/api/days/{day_number}/validate',
      description: 'Validate user answer (legacy)',
      requiresAuth: false,
      params: { day_number: '1' },
      body: { answer: 'paris' }
    },

    // New AI-Powered Endpoints
    'POST /api/submit': {
      method: 'POST',
      url: '/api/submit',
      description: 'Submit answer with AI validation',
      requiresAuth: true,
      params: {},
      body: {
        day: 1,
        promptUsed: "Complete the first challenge",
        response: "I completed the challenge by...",
        transcript: "User: Help me solve...",
        companyGameId: 1,
        files: [
          {
            name: "screenshot.png",
            url: "https://example.com/image.png",
            type: "image/png"
          }
        ]
      }
    },

    // Additional Content Endpoints
    'POST /api/content': {
      method: 'POST',
      url: '/api/content',
      description: 'Get content for specific day using POST request (returns: content, contentType, day, title, contentFlags)',
      requiresAuth: true,
      params: {},
      body: {
        day: 5,
        gameCompanyId: 12345
      }
    },
    'GET /api/days/<day_number>/unlock-status': {
      method: 'GET',
      url: '/api/days/{day_number}/unlock-status',
      description: 'Check if a specific day is unlocked',
      requiresAuth: true,
      params: { day_number: '1' }
    },
    'GET /api/days/<day_number>/assets/<filename>': {
      method: 'GET',
      url: '/api/days/{day_number}/assets/{filename}',
      description: 'Serve static assets from day folder',
      requiresAuth: true,
      params: { day_number: '1', filename: 'image.jpg' }
    }
  };

  const handleEndpointSelect = (endpointKey) => {
    setSelectedEndpoint(endpointKey);
    const endpoint = endpoints[endpointKey];
    setCustomParams(endpoint.params || {});
    setRequestData(endpoint.body ? JSON.stringify(endpoint.body, null, 2) : '');
    setResponse(null);
    setError(null);
  };

  const handleParamChange = (key, value) => {
    setCustomParams(prev => ({ ...prev, [key]: value }));
  };

  const makeRequest = async () => {
    if (!selectedEndpoint) return;

    const endpoint = endpoints[selectedEndpoint];
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Use the same API base URL as other components
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      let url = `${API_BASE_URL}${endpoint.url}`;
      
      // Replace path parameters
      Object.keys(customParams).forEach(key => {
        if (endpoint.url.includes(`{${key}}`)) {
          url = url.replace(`{${key}}`, customParams[key]);
        }
      });

      // Add query parameters
      const queryParams = new URLSearchParams();
      Object.keys(customParams).forEach(key => {
        if (!endpoint.url.includes(`{${key}}`)) {
          queryParams.append(key, customParams[key]);
        }
      });
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const options = {
        method: endpoint.method,
        headers: {
          'Content-Type': 'application/json',
        }
      };

      console.log('Making request to:', url);
      console.log('API_BASE_URL used:', API_BASE_URL);
      console.log('Request options:', options);

      // Add authentication header if required
      if (endpoint.requiresAuth) {
        const tokenInfo = getTokenInfo();
        if (tokenInfo && tokenInfo.token) {
          options.headers['Authorization'] = `Bearer ${tokenInfo.token}`;
        }
      }

      // Add request body for POST/PATCH requests
      if (requestData && (endpoint.method === 'POST' || endpoint.method === 'PATCH')) {
        options.body = requestData;
      }

      const response = await fetch(url, options);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, get as text
        const text = await response.text();
        data = {
          error: 'Non-JSON response received',
          contentType: contentType,
          body: text.substring(0, 500) + (text.length > 500 ? '...' : '')
        };
      }

      setResponse({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: data
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderParamInputs = () => {
    const endpoint = endpoints[selectedEndpoint];
    if (!endpoint || !endpoint.params) return null;

    return (
      <div className="param-inputs">
        <h4>Parameters:</h4>
        {Object.keys(endpoint.params).map(key => (
          <div key={key} className="param-input">
            <label htmlFor={key}>{key}:</label>
            <input
              id={key}
              type="text"
              value={customParams[key] || ''}
              onChange={(e) => handleParamChange(key, e.target.value)}
              placeholder={endpoint.params[key]}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="api-test-page">
      <h1>API Test Page</h1>
      
      <div className="auth-status">
        <h3>Authentication Status</h3>
        <p><strong>Token:</strong> {getTokenInfo() ? 'Present' : 'Not present'}</p>
        <p><strong>User:</strong> {userProfile ? `${userProfile.display_name || userProfile.name} (${userProfile.email})` : 'Not authenticated'}</p>
      </div>

      <div className="endpoint-selector">
        <h3>Select Endpoint</h3>
        <select 
          value={selectedEndpoint} 
          onChange={(e) => handleEndpointSelect(e.target.value)}
          className="endpoint-dropdown"
        >
          <option value="">Choose an endpoint...</option>
          {Object.keys(endpoints).map(key => (
            <option key={key} value={key}>{key}</option>
          ))}
        </select>
      </div>

      {selectedEndpoint && (
        <div className="endpoint-details">
          <h3>Endpoint Details</h3>
          <div className="endpoint-info">
            <p><strong>Method:</strong> {endpoints[selectedEndpoint].method}</p>
            <p><strong>URL:</strong> {endpoints[selectedEndpoint].url}</p>
            <p><strong>Description:</strong> {endpoints[selectedEndpoint].description}</p>
            <p><strong>Requires Auth:</strong> {endpoints[selectedEndpoint].requiresAuth ? 'Yes' : 'No'}</p>
          </div>

          {renderParamInputs()}

          {/* Show example response for content endpoints */}
          {(endpoints[selectedEndpoint].url.includes('/content') || 
            endpoints[selectedEndpoint].url.includes('/api/content')) && (
            <div className="example-response">
              <h4>Expected Response Format:</h4>
              <pre>{JSON.stringify({
                content: "## Brainstorming together\r\nAsk AI to brainstorm...",
                contentType: "markdown",
                day: 6,
                title: "Idea Brainstorm",
                contentFlags: {
                  response: true,
                  transcript: false,
                  promptUsed: true,
                  files: false
                }
              }, null, 2)}</pre>
            </div>
          )}

          {(endpoints[selectedEndpoint].method === 'POST' || endpoints[selectedEndpoint].method === 'PATCH') && (
            <div className="request-body">
              <h4>Request Body:</h4>
              <textarea
                value={requestData}
                onChange={(e) => setRequestData(e.target.value)}
                placeholder="Enter JSON request body..."
                rows={10}
              />
            </div>
          )}

          <button 
            onClick={makeRequest} 
            disabled={loading}
            className="test-button"
          >
            {loading ? 'Testing...' : 'Test Endpoint'}
          </button>
        </div>
      )}

      {response && (
        <div className="response-section">
          <h3>Response</h3>
          <div className="response-info">
            <p><strong>Status:</strong> {response.status} {response.statusText}</p>
            <p><strong>Headers:</strong></p>
            <pre>{JSON.stringify(response.headers, null, 2)}</pre>
            <p><strong>Data:</strong></p>
            <pre>{JSON.stringify(response.data, null, 2)}</pre>
          </div>
        </div>
      )}

      {error && (
        <div className="error-section">
          <h3>Error</h3>
          <p className="error-message">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ApiTestPage;
